// eslint-disable-next-line max-classes-per-file
import React, { useState, useEffect } from 'react';
import { Question, Serializer } from 'survey-core';
import { SurveyQuestionElementBase, ReactQuestionFactory } from 'survey-react-ui';
import { PropertyGridEditorCollection } from 'survey-creator-core';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';

// MUI Components
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

// MUI Icons
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';

import './TipTapEditor.css';

const CUSTOM_TYPE = 'tiptap';

// Link Dialog Component
const LinkDialog = ({ open, onClose, onSave }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const handleUrlChange = (e) => {
    setUrl(e.target.value);
    if (error) setError('');
  };

  const handleSave = () => {
    // Basic URL validation
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    // Check if URL starts with http:// or https://
    if (!/^https?:\/\//.test(url)) {
      setError('URL must start with http:// or https://');
      return;
    }

    onSave(url);
    setUrl('');
    setError('');
  };

  const handleClose = () => {
    setUrl('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Insert Link</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="url"
          label="URL"
          type="url"
          fullWidth
          variant="outlined"
          value={url}
          onChange={handleUrlChange}
          error={!!error}
          helperText={error}
          placeholder="https://example.com"
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export class QuestionTipTapModel extends Question {
  // eslint-disable-next-line class-methods-use-this
  getType() {
    return CUSTOM_TYPE;
  }

  get height() {
    return this.getPropertyValue('height');
  }

  set height(val) {
    this.setPropertyValue('height', val);
  }
}

// Add question type metadata for further serialization into JSON
Serializer.addClass(CUSTOM_TYPE, [{ name: 'height', default: '300px', category: 'layout' }], () => new QuestionTipTapModel(''), 'question');

const HeadingMenu = ({ editor }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (!editor) {
    return null;
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleHeading = (level) => {
    if (level === 0) {
      // Set to paragraph (remove heading)
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level }).run();
    }
    handleClose();
  };

  const headingConfig = [
    { level: 0, label: 'Paragraph', type: 'paragraph' },
    { level: 1, label: 'Heading 1', type: 'heading' },
    { level: 2, label: 'Heading 2', type: 'heading' },
    { level: 3, label: 'Heading 3', type: 'heading' },
    { level: 4, label: 'Heading 4', type: 'heading' },
    { level: 5, label: 'Heading 5', type: 'heading' },
    { level: 6, label: 'Heading 6', type: 'heading' }
  ];

  const getCurrentFormatText = () => {
    const currentFormat = headingConfig.find((item) =>
      item.level === 0 ? editor.isActive('paragraph') : editor.isActive('heading', { level: item.level })
    );
    return currentFormat?.label || 'Text';
  };

  return (
    <div>
      <Tooltip title="Text Format">
        <IconButton
          onClick={handleClick}
          color={open ? 'primary' : 'default'}
          size="small"
          sx={{
            minWidth: 110,
            justifyContent: 'space-between',
            px: 1
          }}
        >
          <span style={{ fontSize: '0.875rem', margin: '0 8px' }}>{getCurrentFormatText()}</span>
          <ArrowDropDown />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 150 }
        }}
      >
        <MenuItem onClick={() => handleHeading(0)} selected={editor.isActive('paragraph')}>
          <ListItemText primary="Paragraph" secondary="Normal text" />
        </MenuItem>

        <MenuItem onClick={() => handleHeading(1)} selected={editor.isActive('heading', { level: 1 })}>
          <ListItemText primary="Heading 1" secondary="Large section heading" />
        </MenuItem>

        <MenuItem onClick={() => handleHeading(2)} selected={editor.isActive('heading', { level: 2 })}>
          <ListItemText primary="Heading 2" secondary="Medium section heading" />
        </MenuItem>

        <MenuItem onClick={() => handleHeading(3)} selected={editor.isActive('heading', { level: 3 })}>
          <ListItemText primary="Heading 3" secondary="Small section heading" />
        </MenuItem>

        <MenuItem onClick={() => handleHeading(4)} selected={editor.isActive('heading', { level: 4 })}>
          <ListItemText primary="Heading 4" secondary="Subsection heading" />
        </MenuItem>
      </Menu>
    </div>
  );
};

// TipTap Menu component for the toolbar
const MenuBar = ({ editor, disabled, onOpenLinkDialog }) => {
  if (!editor || disabled) {
    return null;
  }

  return (
    <div className="tiptap-menubar">
      <div className="group">
        <HeadingMenu editor={editor} />
      </div>

      <div className="group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Bold"
        >
          <FormatBoldIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italic"
        >
          <FormatItalicIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'is-active' : ''}
          title="Underline"
        >
          <FormatUnderlinedIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          title="Strikethrough"
        >
          <StrikethroughSIcon />
        </button>
      </div>

      <div className="group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Ordered List"
        >
          <FormatListNumberedIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet List"
        >
          <FormatListBulletedIcon />
        </button>
      </div>

      <div className="group">
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
          title="Align Left"
        >
          <FormatAlignLeftIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
          title="Align Center"
        >
          <FormatAlignCenterIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
          title="Align Right"
        >
          <FormatAlignRightIcon />
        </button>
      </div>

      <div className="group">
        <button type="button" onClick={onOpenLinkDialog} className={editor.isActive('link') ? 'is-active' : ''} title="Add Link">
          <LinkIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetLink().run()}
          title="Remove Link"
          disabled={!editor.isActive('link')}
        >
          <LinkOffIcon />
        </button>
      </div>
    </div>
  );
};

// TipTap Editor component
export const TipTapEditor = ({ data, onChange, onChangeText, disabled }) => {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6]
        }
      }),
      Link.configure({
        openOnClick: false,
        validate: (href) => /^https?:\/\//.test(href)
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      })
    ],
    content: data || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
      if (onChangeText) {
        onChangeText(editor.getText());
      }
    }
  });

  // update editable state when disabled changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  const handleLinkSave = (url) => {
    editor.chain().focus().setLink({ href: url }).run();
    setLinkDialogOpen(false);
  };

  return (
    <div className="tiptap-editor-container">
      <MenuBar editor={editor} disabled={disabled} onOpenLinkDialog={() => setLinkDialogOpen(true)} />
      <EditorContent editor={editor} />
      <LinkDialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} onSave={handleLinkSave} />
    </div>
  );
};

// Create a class that renders TipTap Editor
export class SurveyQuestionTipTap extends SurveyQuestionElementBase {
  get question() {
    return this.questionBase;
  }

  get value() {
    return this.question.value;
  }

  handleValueChange = (val) => {
    this.question.value = val;
  };

  get style() {
    return { height: this.question.height };
  }

  renderEditor() {
    return <TipTapEditor data={this.value || ''} onChange={this.handleValueChange} />;
  }

  renderElement() {
    return <div style={this.style}>{this.renderEditor()}</div>;
  }
}

// Register `SurveyQuestionTipTap` as a class that renders `tiptap` questions
ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => React.createElement(SurveyQuestionTipTap, props));

// Register `tiptap` as an editor for properties of the `html` type only in the Survey Creator's Property Grid
export const registerHtmlEditor = () => {
  PropertyGridEditorCollection.register({
    fit(prop) {
      // Added null check to ensure prop and prop.type are defined before comparison
      return prop && prop.type && prop.type === 'html';
    },
    getJSON() {
      return { type: CUSTOM_TYPE };
    }
  });
};
